import { useCallback, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './ExpandableText.module.scss';

export function ExpandableText({ text, highlights, length = 100, textClassName = '', onExpand = () => {}, isLabel = false, showFullText = false}) {
  const [isExpanded, setIsExpanded] = useState(showFullText);
  const { t } = useTranslation();

  const formatURL = (url, maxLength = 50) => {
    if (url.length <= maxLength) {
      return url;
    }
    const parts = [];
    let remaining = url;
  
    while (remaining.length > maxLength) {
      parts.push(remaining.slice(0, maxLength));
      remaining = remaining.slice(maxLength);
    }
  
    parts.push(remaining); // Add the last part
    return parts.join('\n'); // Join with a newline to make parts visually distinct
  };
  
  const detectAndFormatURLs = (text, maxLength = 50) => {
    let purifiedText = DOMPurify.sanitize(text, { ALLOWED_TAGS: ['mark', 'span', 'br'],  ALLOWED_ATTR: ['class']});
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return purifiedText?.split(urlRegex).map((part, index) => {
      if (urlRegex.test(part)) {
        return formatURL(part, maxLength)
      }
      return part; // Non-URL text remains as-is
    });
  };

  const URLTextFormatter = ({ text, maxLength = 50 }) => {
    return detectAndFormatURLs(text, maxLength)
  };

  const URLText = URLTextFormatter({ text, maxLength: 100 }).join(' ');
  const newText = URLText?.replace(/\n+(\s*\n+)?/g, '<br/>').split('<br/>');
  const sanitizedText = newText.map(item => DOMPurify.sanitize(item, { ALLOWED_TAGS: ['mark', 'span', 'br'],  ALLOWED_ATTR: ['class']}).trim());

  const nerTypesList = [
    {value: 'KEYWORDS', label: 'Matched keywords'},
    {value: 'PERSON', label: 'Persons'},
    {value: 'ORGANIZATION', label: 'Organizations'},
    {value: 'LOCATION', label: 'Locations'}
  ]

  useEffect(() => {
    if(!highlights) {
      return
    }

    const keywords = document.querySelectorAll('.keywords-highlight');
    const ner = document.querySelectorAll('.ner-highlight');
    const person = document.querySelectorAll('.ner-highlight[data-ner-type="Person"]');
    const location = document.querySelectorAll('.ner-highlight[data-ner-type="Location"]');
    const organization = document.querySelectorAll('.ner-highlight[data-ner-type="Organization"]');
  

    if (highlights.includes('KEYWORDS')) {
      keywords.forEach(item => {
        item.classList.add('active')
      })
    } else {
      keywords.forEach(item => {
        item.classList.remove('active')
      })
    }

    if (highlights.includes('PERSON')) {
      person.forEach(item => {
        item.classList.add('active')
      })
    } else {
      person.forEach(item => {
        item.classList.remove('active')
      })
    }

    if (highlights.includes('LOCATION')) {
      location.forEach(item => {
        item.classList.add('active')
      })
    } else {
      location.forEach(item => {
        item.classList.remove('active')
      })
    }

    if (highlights.includes('ORGANIZATION')) {
      organization.forEach(item => {
        item.classList.add('active')
      })
    } else {
      organization.forEach(item => {
        item.classList.remove('active')
      })
    }

   }, [isExpanded, highlights, text])

    const getVisibleLength = (html) => {
      let count = 0;
      for (let i = 0; i < html.length; i++) {
        if (html[i] === '<') {
          while (i < html.length && html[i] !== '>') i++;
          continue;
        }
        count++;
      }
      return count;
    };

    const cutString = (text, maxLength) => {
      let count = 0;
      let result = '';

      for (let i = 0; i < text.length; i++) {
        if (text[i] === '<') {
          while (text[i] !== '>') {
            result += text[i];
            i++;
          }
          result += text[i];
          continue;
        }

        result += text[i];
        count++;

        if (count === maxLength) {
          break;
        }
      }

      return result;
    };


  const clean = useMemo(
    // () => DOMPurify.sanitize(newText, { ALLOWED_TAGS: ['mark'] }),
    () => {
      return sanitizedText;
    },
    [sanitizedText],
  );

  const shorten = useMemo(
    () => cutString(DOMPurify.sanitize(text, { ALLOWED_TAGS: ['mark', 'span', 'br'],  ALLOWED_ATTR: ['class']}), length) 
        // .replace(/<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g, '')
        // .substring(0, 100)
        .trim(),
    [text],
  );

  const isCollapsible = getVisibleLength(DOMPurify.sanitize(text, { ALLOWED_TAGS: ['mark', 'span', 'br'], ALLOWED_ATTR: ['class'] })) > length;

  const handleTextClick = useCallback((e) => {
    if (!isCollapsible) return;

    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;

    e.stopPropagation();
    onExpand(!isExpanded);
    setIsExpanded(!isExpanded);
  }, [isCollapsible, isExpanded, onExpand]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (e.key === ' ') e.preventDefault();
      handleTextClick(e);
    }
  }, [handleTextClick]);

  return (
    <div className={styles.root}>
      <span
        className={cn(styles.text, textClassName, { [styles.collapsible]: isCollapsible })}
        onClick={handleTextClick}
        onKeyDown={isCollapsible ? handleKeyDown : undefined}
        role={isCollapsible ? 'button' : undefined}
        tabIndex={isCollapsible ? 0 : undefined}
      >
        {isExpanded ? (
        clean.map((item, index) => <p key={`message_paragraph_${index}`} dangerouslySetInnerHTML={{ __html: item }}></p>)
        ) : (
          <p dangerouslySetInnerHTML={{ __html: shorten + (isCollapsible ? '...' : '') }}></p>
        )}
        {isCollapsible && (
          <span className={styles.label}>
            {isExpanded ? t('Show less') : t('Show more')}
          </span>
        )}
      </span>
    </div>
  );
}
